import React, { useEffect, useState } from 'react';
import { Card, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDashboardMonthlyCountQuery } from '../Dashboard/dashoardApi';
import { getSecureItem } from "../../utils/secureStorage";

const WhatsNew = () => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Get user once and extract only the needed boolean property
  const userData = getSecureItem('UserData');
  const hasExpressAccess = userData?.isOpticianExpressAccess === true;

  // Fetch dashboard data with refetchOnMountOrArgChange option
  const {
    data,
    isLoading,
    error,
    refetch
  } = useDashboardMonthlyCountQuery({}, {
    refetchOnMountOrArgChange: true // This ensures data is fetched on mount
  });

  // Process data
  useEffect(() => {
    if (data) {
      const processedData = [
        { name: 'Patients', value: data.newPatients || 0 },
        { name: 'Standard', value: data.newStandardTests || 0 }
      ];

      // ✅ Add Express ONLY if access is allowed
      if (hasExpressAccess) {
        processedData.push({
          name: 'Express',
          value: data.newExpressTests || 0
        });
      }

      setChartData(processedData);
      setIsInitialLoad(false);
    }
  }, [data, hasExpressAccess]); // Boolean is stable and won't cause re-renders

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        await refetch();
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchData();
  }, [refetch]);

  // Calculate dashboard data with safe defaults
  const dashboardData = {
    patients: data?.newPatients || 0,
    standard: data?.newStandardTests || 0,
    express: data?.newExpressTests || 0
  };

  // Get current month and year
  const currentDate = new Date();
  const currentMonth = currentDate.toLocaleString('default', { month: 'short' });
  const currentYear = currentDate.getFullYear();

  // Handle refresh button click
  const handleRefresh = async () => {
    try {
      await refetch();
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  // Determine if we're showing loading state
  const showLoading = (isLoading && isInitialLoad) || false;
  const showError = error && !isLoading;

  return (
    <Card className="mb-3 shadow-sm" style={{ height: '275px' }}>
      <Card.Header
        className="bg-transparent d-flex align-items-center"
        style={{ height: '50px' }}
      >
        <div className="d-flex align-items-center">
          <Card.Title className="mb-0">
            Vos chiffres du mois
          </Card.Title>
          <span className="text-muted ms-2 ml-2 p-4" style={{ fontSize: '0.8rem' }}>
            {currentMonth} {currentYear}
          </span>
        </div>
        <button
          className="btn btn-sm btn-light border py-1 px-2"
          onClick={handleRefresh}
          disabled={isLoading}
          style={{ fontSize: '0.85rem' }}
        >
          {isLoading ? '...' : 'rafraîchir'}
        </button>
      </Card.Header>

      <Card.Body className="p-3">
        {showLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
            <div className="mt-2" style={{ fontSize: '0.8rem' }}>
              Loading statistics...
            </div>
          </div>
        ) : showError ? (
          <Alert variant="danger" className="py-2" style={{ fontSize: '0.85rem' }}>
            Failed to load statistics. Please try again.
            <div className="mt-2">
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={handleRefresh}
              >
                Retry
              </button>
            </div>
          </Alert>
        ) : (
          <>
            {/* Mini stats in one row */}
            <Row className="mb-3 g-2 text-center">
              <Col xs={4}>
                <div className="border rounded p-2">
                  <div className="text-primary fw-bold" style={{ fontSize: '1.5rem' }}>
                    {dashboardData.patients}
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Patients
                  </div>
                </div>
              </Col>
              <Col xs={4}>
                <div className="border rounded p-2">
                  <div className="text-success fw-bold" style={{ fontSize: '1.5rem' }}>
                    {dashboardData.standard}
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Tests
                  </div>
                </div>
              </Col>
              {hasExpressAccess && (
                <Col xs={4}>
                  <div className="border rounded p-2">
                    <div className="text-warning fw-bold" style={{ fontSize: '1.5rem' }}>
                      {dashboardData.express}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Express
                    </div>
                  </div>
                </Col>
              )}
            </Row>

            {/* Simple bar chart visualization without recharts */}
            <div className="mt-4 pt-2">
              {chartData.some(item => item.value > 0) ? (
                <div className="d-flex align-items-end" style={{ height: '100px' }}>
                  {chartData.map((item, index) => {
                    const maxValue = Math.max(...chartData.map(d => d.value));
                    const height = maxValue > 0 ? (item.value / maxValue) * 80 : 0;

                    return (
                      <div key={index} className="flex-fill d-flex flex-column align-items-center mx-1">
                        <div className="text-center" style={{ fontSize: '0.7rem', height: '20px' }}>
                          {item.value}
                        </div>
                        <div
                          className="rounded-top"
                          style={{
                            width: '80%',
                            height: `${height}px`,
                            backgroundColor: index === 0 ? '#072cab' : index === 1 ? '#198754' : '#ffc107',
                            minHeight: item.value > 0 ? '2px' : '0'
                          }}
                        ></div>
                        {/* <div className="text-center mt-1" style={{ fontSize: '0.7rem' }}>
                          {item.name}
                        </div> */}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-3" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                  No activity this month
                  <div className="mt-1">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={handleRefresh}
                      style={{ fontSize: '0.7rem' }}
                    >
                      Check again
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default WhatsNew;